@file:OptIn(kotlin.uuid.ExperimentalUuidApi::class)

package com.yogaaicoach.pages.workout

import com.yogaaicoach.common.core.extensions.format
import com.yogaaicoach.common.ui.components.*
import com.yogaaicoach.pages.workout.data.models.Exercise
import com.yogaaicoach.pages.workout.data.models.WorkoutProgressSectionDto
import com.yogaaicoach.pages.workout.workout.ui.WorkoutState
import com.yogaaicoach.platform.WORKOUT_AUDIO_ID
import com.yogaaicoach.platform.WORKOUT_VIDEO_A_ID
import com.yogaaicoach.platform.WORKOUT_VIDEO_B_ID
import com.yogaaicoach.platform.loadWorkoutVideo
import com.yogaaicoach.platform.releaseWorkoutVideos
import com.yogaaicoach.platform.resetWorkoutVideoSlots
import com.yogaaicoach.scope
import com.yogaaicoach.user.AppViewModel
import kotlin.js.json
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.audio
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.video
import web.dom.ElementId
import react.useEffect
import react.useEffectOnce
import react.useState
import web.cssom.ClassName
import web.window.window
import kotlin.time.Duration.Companion.seconds
import kotlin.uuid.Uuid

internal external interface WorkoutPageProps : Props {
    var viewModel: WorkoutScreenViewModel
    var appViewModel: AppViewModel
    var workoutGuid: String
    var startExerciseNumber: Int
    var onWorkoutCompleted: () -> Unit
    var onNavigateBack: () -> Unit
    var onNavigateToDashboard: () -> Unit
    var onNavigateToPayment: () -> Unit
    var onNavigateToContactUs: () -> Unit
}

internal val WorkoutPage = FC<WorkoutPageProps> { props ->
    var isPageLoaded by useState(false)
    var workoutState by useState(WorkoutState.NOT_STARTED)
    var currentExercise by useState<Exercise?>(null)
    var nextExercise by useState<Exercise?>(null)
    var countdownSeconds by useState(0)
    var isPaused by useState(false)
    var isFirstExercise by useState(true)
    var isCurrentExerciseHeavy by useState(false)
    var sections by useState(emptyList<WorkoutProgressSectionDto>())
    var videoUrl by useState<String?>(null)
    var audioUrl by useState<String?>(null)
    var showQuitDialog by useState(false)
    var pausedForQuitDialog by useState(false)
    var toastMessage by useState<String?>(null)
    var videoHeightPerWidth by useState<Double?>(null)
    var hasVideoFrame by useState(false)

    useEffectOnce {
        if (props.workoutGuid.isEmpty()) {
            props.onNavigateToDashboard()
            return@useEffectOnce
        }
        scope.launch { props.viewModel.isPageLoaded.collect { isPageLoaded = it } }
        scope.launch { props.viewModel.currentExercise.collect { currentExercise = it } }
        scope.launch { props.viewModel.nextExercise.collect { nextExercise = it } }
        scope.launch { props.viewModel.countdownSeconds.collect { countdownSeconds = it } }
        scope.launch { props.viewModel.isPaused.collect { isPaused = it } }
        scope.launch { props.viewModel.isFirstExercise.collect { isFirstExercise = it } }
        scope.launch { props.viewModel.isCurrentExerciseHeavy.collect { isCurrentExerciseHeavy = it } }
        scope.launch { props.viewModel.sections.collect { sections = it } }
        // Every new URL means the video element has no frame to paint until it loads again,
        // so the dark surface has to go back to hidden here — not just on first mount.
        scope.launch { props.viewModel.videoUrl.collect { videoUrl = it; hasVideoFrame = false } }
        scope.launch { props.viewModel.audioUrl.collect { audioUrl = it } }
        scope.launch {
            props.viewModel.workoutState.collect { state ->
                workoutState = state
                if (state == WorkoutState.COMPLETED) props.onWorkoutCompleted()
            }
        }
        props.viewModel.init(
            workoutGuid = Uuid.parse(props.workoutGuid),
            startExerciseNumber = props.startExerciseNumber,
            isPortrait = window.innerWidth < 600,
        )
    }

    // Which video slot is active is module state in MediaPlayer.js.kt, so it has to be put back
    // to slot A for these freshly mounted elements. useEffect cleanup in these wrappers is
    // coroutine cancellation, so the unmount half goes in a finally block.
    useEffectOnce {
        resetWorkoutVideoSlots()
        try {
            awaitCancellation()
        } finally {
            releaseWorkoutVideos()
        }
    }

    // The video slots carry no React-controlled src (see MediaPlayer.js.kt) — pointing the active
    // slot at a new exercise's first part is an imperative call, not an attribute React renders.
    // isPageLoaded is a dependency because the slots only exist once the loading screen is gone,
    // and the first exercise's URL arrives before that flips: without it the very first load
    // would find no element, never report ready, and the countdown would never start.
    useEffect(videoUrl, isPageLoaded) {
        val url = videoUrl ?: return@useEffect
        loadWorkoutVideo(url) { element ->
            if (element.videoWidth > 0) {
                videoHeightPerWidth = element.videoHeight.toDouble() / element.videoWidth.toDouble()
            }
            // canplay means a frame is decoded, i.e. the earliest moment the element paints
            // something — showing the dark surface any earlier leaves a black box.
            hasVideoFrame = true
            props.viewModel.onVideoReady()
        }
    }

    // Single root div so the flex column layout can drive height from here down.
    div {
        className = ClassName("workout-page-shell")

        TopBar {
            title = currentExercise?.baseData?.name ?: "Workout"
            onSignOut = { props.appViewModel.signOut() }
            onNavigateToDashboard = props.onNavigateToDashboard
            onNavigateToPayment = props.onNavigateToPayment
            onNavigateToContactUs = props.onNavigateToContactUs
        }

        if (!isPageLoaded) {
            LoadingPage {}
        } else {
            // Video — fills the height between TopBar and controls (flex: 1 in CSS), and the
            // width is derived from the video's real aspect ratio (measured once it can play)
            // so the container is exactly as wide as the video needs. max-height keeps it from
            // exceeding the viewport width on narrow screens, where width is the constraint.
            div {
                id = ElementId("workout-video-container")
                className = ClassName(
                    if (hasVideoFrame) "workout-video-container workout-video-container-ready"
                    else "workout-video-container"
                )
                videoHeightPerWidth?.let { ratio ->
                    style = json(
                        "width" to "auto",
                        "aspectRatio" to "${1.0 / ratio}",
                        "maxHeight" to "calc(100vw * $ratio)",
                    ) as react.CSSProperties
                }
                // Two stacked slots so an exercise's next part (intro → loop → outro) can buffer
                // and decode in the hidden one while the current part still plays; swapVideos()
                // then transitions by toggling display, with no src change and no decoder reset.
                // Both are always rendered so the child indices below never shift.
                // loop = false is essential: a looping element never fires `ended`, which is what
                // the whole transition chain in VideoTransitionBuilder hangs off.
                video {
                    id = ElementId(WORKOUT_VIDEO_A_ID)
                    className = ClassName("workout-video")
                    muted = true
                    loop = false
                    playsInline = true
                    preload = "auto"
                }
                video {
                    id = ElementId(WORKOUT_VIDEO_B_ID)
                    className = ClassName("workout-video workout-video-hidden")
                    muted = true
                    loop = false
                    playsInline = true
                    preload = "auto"
                }

                // Overlay comes AFTER the videos so React keeps them at child-indices 0 and 1
                // in both states. If the overlay preceded them, React would see index 0 change
                // from <video> to <div> and remount both slots — losing their buffered parts
                // and the imperatively assigned srcs mid-exercise.
                if (isPaused) {
                    div {
                        className = ClassName("workout-paused-overlay")
                        span { className = ClassName("workout-paused-label"); +"PAUSED" }
                    }
                }
            }

            // Hidden audio element. No autoPlay — the ViewModel starts video and audio together
            // once both report ready, otherwise the narration runs ahead of the video.
            // preload "auto" because the default "metadata" only fetches headers, and canplay
            // then never fires on slow mobile connections.
            audioUrl?.let { url ->
                audio {
                    id = ElementId(WORKOUT_AUDIO_ID)
                    src = url
                    autoPlay = false
                    preload = "auto"
                    onCanPlay = { props.viewModel.onAudioReady() }
                }
            }

            div {
                className = ClassName("workout-screen-shell")

                // Exercise name — UPPERCASE per Compose source (toUpperCase(Locale.current))
                p {
                    className = ClassName("workout-screen-exercise-name")
                    +(currentExercise?.baseData?.name?.uppercase() ?: "")
                }

                // Next exercise
                val next = nextExercise
                if (next != null) {
                    div {
                        className = ClassName("workout-screen-next-row")
                        span { className = ClassName("workout-screen-next-label"); +"Next: " }
                        val nextName = if (next.baseData.isOtherSide)
                            "${next.baseData.name} (other side)" else next.baseData.name
                        span { className = ClassName("workout-screen-next-value"); +nextName }
                    }
                } else {
                    p { className = ClassName("workout-screen-next-row workout-screen-last-exercise"); +"Last exercise" }
                }

                // Timer row — 3-column grid: spacer | timer | chip (never overlaps)
                div {
                    className = ClassName("workout-screen-timer-row")
                    div {} // left spacer (column 1)
                    span {
                        className = ClassName(
                            if (countdownSeconds in 1..10)
                                "workout-screen-timer workout-screen-timer-urgent"
                            else
                                "workout-screen-timer"
                        )
                        +countdownSeconds.seconds.format()
                    }
                    TooHardChip(isCurrentExerciseHeavy) {
                        val nowHeavy = !isCurrentExerciseHeavy
                        props.viewModel.onMarkHeavy(nowHeavy)
                        scope.launch {
                            toastMessage = if (nowHeavy) "Marked as too hard" else "Mark removed"
                            delay(2500)
                            toastMessage = null
                        }
                    }
                }

                // Heavy feedback toast
                toastMessage?.let { msg ->
                    div { className = ClassName("workout-heavy-toast"); +msg }
                }

                // Playback controls
                div {
                    className = ClassName("workout-screen-controls")
                    WorkoutControlButton(
                        label = "Previous",
                        enabled = !isFirstExercise,
                        icon = SkipPreviousIcon,
                        onClick = { props.viewModel.onPreviousExercise() }
                    )
                    button {
                        className = ClassName("workout-play-pause-btn")
                        onClick = { props.viewModel.toggleIsPaused() }
                        if (isPaused) PlayCircleIcon {} else PauseCircleIcon {}
                    }
                    WorkoutControlButton(
                        label = "Skip",
                        enabled = true,
                        icon = SkipNextIcon,
                        onClick = { props.viewModel.onSkipExercise() }
                    )
                }

                // Section progress
                val currentSectionIndex = currentExercise?.sectionIndex ?: 0
                WorkoutSectionProgress(sections, currentSectionIndex)

                // Quit — wrapped in a separator section so the destructive action is
                // visually isolated from the section dots above it.
                div {
                    className = ClassName("workout-quit-section")
                    button {
                        className = ClassName("text-button workout-quit-btn")
                        onClick = {
                            // Only pause if not already paused; track whether we did so we can
                            // undo it on dismiss without disturbing a pre-existing paused state.
                            val didPause = !isPaused
                            if (didPause) props.viewModel.toggleIsPaused()
                            pausedForQuitDialog = didPause
                            showQuitDialog = true
                        }
                        +"Quit Workout"
                    }
                }
            }

            // Dialog rendered last so it never shifts the video container's child index.
            // It uses position:fixed so DOM order has no effect on visual stacking.
            if (showQuitDialog) {
                QuitWorkoutDialog(
                    onConfirm = {
                        showQuitDialog = false
                        pausedForQuitDialog = false
                        props.viewModel.reset()
                        props.onNavigateBack()
                    },
                    onDismiss = {
                        showQuitDialog = false
                        if (pausedForQuitDialog) props.viewModel.toggleIsPaused()
                        pausedForQuitDialog = false
                    }
                )
            }
        }
    }
}

private fun ChildrenBuilder.TooHardChip(isHeavy: Boolean, onToggle: () -> Unit) {
    button {
        className = ClassName(
            if (isHeavy) "workout-too-hard-chip workout-too-hard-chip-active"
            else "workout-too-hard-chip"
        )
        onClick = { onToggle() }
        +(if (isHeavy) "Too hard" else "Too hard?")
    }
}

private fun ChildrenBuilder.WorkoutControlButton(
    label: String,
    enabled: Boolean,
    icon: react.FC<Props>,
    onClick: () -> Unit,
) {
    button {
        className = ClassName(
            if (enabled) "workout-control-btn" else "workout-control-btn workout-control-btn-disabled"
        )
        disabled = !enabled
        this.onClick = { onClick() }
        icon {}
        span { +label }
    }
}

// Section progress — compact dots on small screens, wide pills on large screens.
// Both are always rendered; CSS hides one per breakpoint (mirrors WorkoutSectionProgress.kt's
// isPortrait branch, but driven by CSS media query instead of runtime screen-size detection).
private fun ChildrenBuilder.WorkoutSectionProgress(
    sections: List<WorkoutProgressSectionDto>,
    currentSectionIndex: Int,
) {
    if (sections.isEmpty()) return

    // Compact dots (≤599px)
    div {
        className = ClassName("workout-section-progress-compact")
        div {
            className = ClassName("workout-section-dots-row")
            sections.forEachIndexed { index, section ->
                val state = when {
                    index < currentSectionIndex -> "completed"
                    index == currentSectionIndex -> "current"
                    else -> "upcoming"
                }
                div { className = ClassName("workout-section-dot workout-section-dot-$state") }
                if (index < sections.lastIndex) {
                    div { className = ClassName("workout-section-connector${if (state == "completed") " workout-section-connector-filled" else ""}") }
                }
            }
        }
        sections.getOrNull(currentSectionIndex)?.let { current ->
            p { className = ClassName("workout-section-label"); +current.name }
        }
    }

    // Wide pills (≥600px)
    div {
        className = ClassName("workout-section-progress-wide")
        div {
            className = ClassName("workout-section-pills-row")
            sections.forEachIndexed { index, section ->
                val state = when {
                    index < currentSectionIndex -> "completed"
                    index == currentSectionIndex -> "current"
                    else -> "upcoming"
                }
                div {
                    className = ClassName("workout-section-pill workout-section-pill-$state")
                    title = section.name // recovers the full name when the label is truncated
                    if (state == "completed") CheckIcon {}
                    span { +section.name }
                }
                if (index < sections.lastIndex) {
                    div { className = ClassName("workout-section-pill-connector${if (state == "completed") " workout-section-connector-filled" else ""}") }
                }
            }
        }
    }
}

private fun ChildrenBuilder.QuitWorkoutDialog(onConfirm: () -> Unit, onDismiss: () -> Unit) {
    div {
        className = ClassName("dialog-backdrop")
        onClick = { onDismiss() }
        div {
            className = ClassName("dialog-box")
            onClick = { event -> event.stopPropagation() }
            h2 { className = ClassName("dialog-title"); +"Quit Workout?" }
            p { className = ClassName("dialog-body"); +"Your progress so far will be saved." }
            div {
                className = ClassName("dialog-button-row")
                button { className = ClassName("text-button"); +"No"; onClick = { onDismiss() } }
                button { className = ClassName("text-button"); +"Yes, Quit"; onClick = { onConfirm() } }
            }
        }
    }
}
